import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import {
  type MovementDoc,
  type MovementDocRow,
  type MovementCurrencyTotals,
  fmtNum,
  fmtCell,
  fmtDocDate,
  rowBalance,
  sortCurrencyTotals,
} from '@/lib/cari-movements-doc';

/**
 * Cari Hareketler — printable report (A4 landscape).
 *
 * Two modes share one document:
 *   - 'list'      : the global movement log across every cari (Cari column shown)
 *   - 'statement' : one cari's ledger / ekstre (running TL balance column shown,
 *                   counterparty identity band above the table)
 *
 * Letterhead with logo, parameter strip, navy table header (repeated per page),
 * zebra rows, an amber "M" tag on manual rows, per-currency turnover totals and
 * a signed BAKİYE line, and a fixed page footer. Uses Roboto for Turkish glyphs.
 */

export function registerMovementFonts(basePath = '/fonts'): void {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: `${basePath}/Roboto-Regular.ttf` },
      { src: `${basePath}/Roboto-Medium.ttf`, fontWeight: 500 },
      { src: `${basePath}/Roboto-Bold.ttf`, fontWeight: 700 },
    ],
  });
}
if (typeof window !== 'undefined') registerMovementFonts();
Font.registerHyphenationCallback((word) => [word]);

const C = {
  ink: '#111827',
  inkSoft: '#4b5563',
  inkFaint: '#9ca3af',
  navy: '#1e3a5f',
  navySoft: '#eef2f7',
  band: '#f3f4f6',
  zebra: '#fafbfc',
  line: '#e5e7eb',
  lineSoft: '#f1f5f9',
  green: '#047857',
  red: '#b91c1c',
  amber: '#b45309',
  amberBg: '#fef3c7',
  white: '#ffffff',
};

const s = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 46,
    paddingHorizontal: 28,
    fontSize: 8,
    fontFamily: 'Roboto',
    color: C.ink,
  },

  // ── Letterhead ────────────────────────────────────────────────────────────
  letterhead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 12,
    marginBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: C.navy,
  },
  brand: { flexDirection: 'row', alignItems: 'center' },
  // Wide enough for the 2:1 trading logo; objectFit keeps the square turizm
  // logo undistorted in the same slot.
  logo: { width: 76, height: 40, objectFit: 'contain', marginRight: 10 },
  companyName: { fontSize: 14, fontWeight: 700, color: C.navy, letterSpacing: 0.4 },
  companyLine: { fontSize: 7.5, color: C.inkSoft, marginTop: 1.5 },
  reportMeta: { alignItems: 'flex-end' },
  reportTitle: { fontSize: 13, fontWeight: 700, color: C.navy, letterSpacing: 0.6 },
  reportPeriod: { fontSize: 9, fontWeight: 500, color: C.ink, marginTop: 3 },
  reportGenerated: { fontSize: 7, color: C.inkFaint, marginTop: 3 },

  // ── Parameter strip ───────────────────────────────────────────────────────
  paramStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: C.navySoft,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: C.navy,
  },
  paramItem: { fontSize: 7.5, color: C.inkSoft, marginRight: 14, marginVertical: 1 },
  paramValue: { fontWeight: 500, color: C.ink },

  // ── Statement counterparty band ───────────────────────────────────────────
  cariBand: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: C.navy,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 2,
  },
  cariBandName: { color: C.white, fontSize: 11, fontWeight: 700 },
  cariBandMeta: { color: '#cdd7e5', fontSize: 8.5, fontWeight: 500 },

  // ── Table ─────────────────────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    backgroundColor: C.navy,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  headerCell: { fontSize: 7.5, fontWeight: 700, color: C.white },

  row: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.lineSoft,
  },
  rowAlt: { backgroundColor: C.zebra },
  cell: { fontSize: 7.5 },
  cellMuted: { color: C.inkSoft },
  num: { textAlign: 'right' },
  manualTag: { color: C.amber, fontWeight: 700 },

  // ── Totals ────────────────────────────────────────────────────────────────
  totalsHeading: {
    marginTop: 10,
    marginBottom: 3,
    fontSize: 8,
    fontWeight: 700,
    color: C.inkSoft,
    letterSpacing: 0.5,
  },
  totalRow: {
    flexDirection: 'row',
    backgroundColor: C.navySoft,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderTopWidth: 0.5,
    borderTopColor: C.line,
  },
  totalCell: { fontSize: 8, fontWeight: 700, color: C.ink },
  totalLabel: { fontSize: 8, fontWeight: 700, color: C.navy, textAlign: 'right', paddingRight: 8 },
  bakiyeRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.line,
  },
  bakiyeText: { fontSize: 8.5, fontWeight: 700 },

  empty: { padding: 24, textAlign: 'center', color: C.inkSoft, fontSize: 9 },

  // ── Footer ────────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: C.line,
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: C.inkFaint },
});

// Column widths per mode (percent strings, summing to 100).
const listCols = {
  no: { width: '3%' },
  date: { width: '7%' },
  cari: { width: '20%' },
  type: { width: '4%' },
  code: { width: '7%' },
  voucher: { width: '8%' },
  desc: { width: '16%' },
  cur: { width: '5%' },
  num: { width: '7.5%' },
} as const;

const stmtCols = {
  no: { width: '3%' },
  date: { width: '8%' },
  voucher: { width: '10%' },
  desc: { width: '28%' },
  cur: { width: '6%' },
  num: { width: '9%' },
  bal: { width: '9%' },
} as const;

interface CariMovementsPDFProps {
  doc: MovementDoc;
  logoUrl?: string;
}

export function CariMovementsPDF({ doc, logoUrl }: CariMovementsPDFProps): React.JSX.Element {
  const { company, meta, data } = doc;
  const isStatement = meta.mode === 'statement';
  const totals = sortCurrencyTotals(data.totalsByCurrency);

  // Header + rows are mode-specific; totals layout shares a helper.
  const listHeader = (
    <View style={s.headerRow} fixed>
      <Text style={[s.headerCell, listCols.no]}>{meta.columns.no}</Text>
      <Text style={[s.headerCell, listCols.date]}>{meta.columns.date}</Text>
      <Text style={[s.headerCell, listCols.cari]}>{meta.columns.cari}</Text>
      <Text style={[s.headerCell, listCols.type]}>{meta.columns.type}</Text>
      <Text style={[s.headerCell, listCols.code]}>{meta.columns.code}</Text>
      <Text style={[s.headerCell, listCols.voucher]}>{meta.columns.voucher}</Text>
      <Text style={[s.headerCell, listCols.desc]}>{meta.columns.description}</Text>
      <Text style={[s.headerCell, listCols.cur]}>{meta.columns.currency}</Text>
      <Text style={[s.headerCell, listCols.num, s.num]}>{meta.columns.fxDebit}</Text>
      <Text style={[s.headerCell, listCols.num, s.num]}>{meta.columns.fxCredit}</Text>
      <Text style={[s.headerCell, listCols.num, s.num]}>{meta.columns.tryDebit}</Text>
      <Text style={[s.headerCell, listCols.num, s.num]}>{meta.columns.tryCredit}</Text>
    </View>
  );

  const stmtHeader = (
    <View style={s.headerRow} fixed>
      <Text style={[s.headerCell, stmtCols.no]}>{meta.columns.no}</Text>
      <Text style={[s.headerCell, stmtCols.date]}>{meta.columns.date}</Text>
      <Text style={[s.headerCell, stmtCols.voucher]}>{meta.columns.voucher}</Text>
      <Text style={[s.headerCell, stmtCols.desc]}>{meta.columns.description}</Text>
      <Text style={[s.headerCell, stmtCols.cur]}>{meta.columns.currency}</Text>
      <Text style={[s.headerCell, stmtCols.num, s.num]}>{meta.columns.fxDebit}</Text>
      <Text style={[s.headerCell, stmtCols.num, s.num]}>{meta.columns.fxCredit}</Text>
      <Text style={[s.headerCell, stmtCols.num, s.num]}>{meta.columns.tryDebit}</Text>
      <Text style={[s.headerCell, stmtCols.num, s.num]}>{meta.columns.tryCredit}</Text>
      <Text style={[s.headerCell, stmtCols.bal, s.num]}>{meta.columns.balance}</Text>
    </View>
  );

  const renderListRow = (r: MovementDocRow, i: number) => (
    <View key={i} style={i % 2 === 1 ? [s.row, s.rowAlt] : s.row} wrap={false}>
      <Text style={[s.cell, s.cellMuted, listCols.no]}>{i + 1}</Text>
      <Text style={[s.cell, listCols.date]}>{fmtDocDate(r.date)}</Text>
      <Text style={[s.cell, listCols.cari]}>{r.counterpartyName}</Text>
      <Text style={[s.cell, s.cellMuted, listCols.type]}>{r.cariType}</Text>
      <Text style={[s.cell, s.cellMuted, listCols.code]}>{r.cariCode}</Text>
      <Text style={[s.cell, listCols.voucher]}>
        {r.voucherNo}
        {r.isManual ? <Text style={s.manualTag}> {meta.manualTag}</Text> : null}
      </Text>
      <Text style={[s.cell, listCols.desc]}>{r.description}</Text>
      <Text style={[s.cell, listCols.cur]}>{r.currency}</Text>
      <Text style={[s.cell, listCols.num, s.num]}>{fmtCell(r.fxDebit)}</Text>
      <Text style={[s.cell, listCols.num, s.num]}>{fmtCell(r.fxCredit)}</Text>
      <Text style={[s.cell, listCols.num, s.num]}>{fmtCell(r.tryDebit)}</Text>
      <Text style={[s.cell, listCols.num, s.num]}>{fmtCell(r.tryCredit)}</Text>
    </View>
  );

  const renderStmtRow = (r: MovementDocRow, i: number) => {
    const bal = rowBalance(r, meta.balanceNative);
    return (
      <View key={i} style={i % 2 === 1 ? [s.row, s.rowAlt] : s.row} wrap={false}>
        <Text style={[s.cell, s.cellMuted, stmtCols.no]}>{i + 1}</Text>
        <Text style={[s.cell, stmtCols.date]}>{fmtDocDate(r.date)}</Text>
        <Text style={[s.cell, stmtCols.voucher]}>
          {r.voucherNo}
          {r.isManual ? <Text style={s.manualTag}> {meta.manualTag}</Text> : null}
        </Text>
        <Text style={[s.cell, stmtCols.desc]}>{r.description}</Text>
        <Text style={[s.cell, stmtCols.cur]}>{r.currency}</Text>
        <Text style={[s.cell, stmtCols.num, s.num]}>{fmtCell(r.fxDebit)}</Text>
        <Text style={[s.cell, stmtCols.num, s.num]}>{fmtCell(r.fxCredit)}</Text>
        <Text style={[s.cell, stmtCols.num, s.num]}>{fmtCell(r.tryDebit)}</Text>
        <Text style={[s.cell, stmtCols.num, s.num]}>{fmtCell(r.tryCredit)}</Text>
        <Text style={[s.cell, stmtCols.bal, s.num, { color: bal >= 0 ? C.green : C.red, fontWeight: 500 }]}>
          {fmtNum(bal)}
        </Text>
      </View>
    );
  };

  const renderTotals = (cur: MovementCurrencyTotals) => {
    const net = cur.currency === 'TRY' ? cur.tryNet : cur.fxNet;
    const label = `${meta.currencyNames[cur.currency] ?? cur.currency} ${meta.totalLabel}`;
    return (
      <View key={cur.currency} wrap={false}>
        <View style={s.totalRow}>
          {isStatement ? (
            <>
              <Text style={[s.totalLabel, { width: '55%' }]}>{label}</Text>
              <Text style={[s.totalCell, stmtCols.num, s.num]}>{fmtNum(cur.fxDebit)}</Text>
              <Text style={[s.totalCell, stmtCols.num, s.num]}>{fmtNum(cur.fxCredit)}</Text>
              <Text style={[s.totalCell, stmtCols.num, s.num]}>{fmtNum(cur.tryDebit)}</Text>
              <Text style={[s.totalCell, stmtCols.num, s.num]}>{fmtNum(cur.tryCredit)}</Text>
              <Text style={[s.totalCell, stmtCols.bal]} />
            </>
          ) : (
            <>
              <Text style={[s.totalLabel, { width: '70%' }]}>{label}</Text>
              <Text style={[s.totalCell, listCols.num, s.num]}>{fmtNum(cur.fxDebit)}</Text>
              <Text style={[s.totalCell, listCols.num, s.num]}>{fmtNum(cur.fxCredit)}</Text>
              <Text style={[s.totalCell, listCols.num, s.num]}>{fmtNum(cur.tryDebit)}</Text>
              <Text style={[s.totalCell, listCols.num, s.num]}>{fmtNum(cur.tryCredit)}</Text>
            </>
          )}
        </View>
        <View style={s.bakiyeRow}>
          <Text style={[s.bakiyeText, { color: net >= 0 ? C.green : C.red }]}>
            {meta.balanceLabel}: {fmtNum(Math.abs(net))} {cur.currency} {net >= 0 ? meta.debitSide : meta.creditSide}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <Document title={meta.title} author={company.name} subject={meta.periodLabel}>
      <Page size="A4" orientation="landscape" style={s.page}>
        {/* Letterhead */}
        <View style={s.letterhead}>
          <View style={s.brand}>
            {logoUrl && <Image src={logoUrl} style={s.logo} />}
            <View>
              <Text style={s.companyName}>{company.name}</Text>
              <Text style={s.companyLine}>{company.address}</Text>
              <Text style={s.companyLine}>
                {company.phone} · {company.email} · {company.web}
              </Text>
            </View>
          </View>
          <View style={s.reportMeta}>
            <Text style={s.reportTitle}>{meta.title}</Text>
            <Text style={s.reportPeriod}>{meta.periodLabel}</Text>
            <Text style={s.reportGenerated}>{meta.generatedLabel}</Text>
          </View>
        </View>

        {/* Parameter strip */}
        {meta.params.length > 0 && (
          <View style={s.paramStrip}>
            {meta.params.map((p) => (
              <Text key={p.label} style={s.paramItem}>
                {p.label}: <Text style={s.paramValue}>{p.value}</Text>
              </Text>
            ))}
          </View>
        )}

        {/* Statement counterparty identity */}
        {isStatement && data.cari && (
          <View style={s.cariBand}>
            <Text style={s.cariBandName}>{data.cari.name}</Text>
            <Text style={s.cariBandMeta}>
              {data.cari.code}
              {data.cari.type ? ` · ${data.cari.type}` : ''}
            </Text>
          </View>
        )}

        {/* Table */}
        {isStatement ? stmtHeader : listHeader}
        {data.rows.length === 0 ? (
          <Text style={s.empty}>{meta.emptyLabel}</Text>
        ) : isStatement ? (
          data.rows.map(renderStmtRow)
        ) : (
          data.rows.map(renderListRow)
        )}

        {/* Per-currency totals */}
        {data.rows.length > 0 && (
          <View>
            <Text style={s.totalsHeading} minPresenceAhead={60}>
              {meta.totalLabel}
            </Text>
            {totals.map(renderTotals)}
          </View>
        )}

        {/* Footer (every page) */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            {company.name} · {meta.title}
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) => meta.pageLabel(pageNumber, totalPages)}
          />
        </View>
      </Page>
    </Document>
  );
}
